/** The teacher's gradebooks, grouped by school year.
 *
 * Every book is the system green; the only per-book customization is the colour
 * of the subject text (via the kebab → Edit). All reads and writes go through
 * `@/gradebook/api` - see that file for the backend seam. */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, ClipboardList, MoreVertical, LucidePencil, LucideTrash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PageContainer } from "@/layouts/PageContainer";
import { PageHeader } from "@/layouts/PageHeader";
import { EmptyState } from "@/components/ui/EmptyState";
import { SpinnerPage } from "@/components/ui/spinner";
import { useGradebooks, useGradebookApi } from "@/gradebook/api";
import { titleColorClass } from "@/gradebook/titleColors";
import {
  CreateGradebookModal, EditGradebookModal, DeleteGradebookModal,
} from "@/components/modals/GradebookModals";

/* The book is always the system green - spine, grade stamp, and stat icons. */
const GREEN = {
  spine: "from-brand-600 to-brand-800",
  chip: "bg-brand-50 text-brand-700 border-brand-200 dark:bg-brand-900 dark:text-brand-300 dark:border-brand-800",
  icon: "text-brand-400",
};

function yearSortKey(year) {
  const match = (year ?? "").match(/\d{4}/);
  return match ? Number(match[0]) : -1;
}

/** Same shape as ReportFolderList: consecutive runs of one school year. */
function groupByYear(items) {
  const sections = [];
  let current;
  for (const item of items) {
    if (item.schoolYear !== current) {
      current = item.schoolYear;
      sections.push({ year: current, items: [] });
    }
    sections[sections.length - 1].items.push(item);
  }
  return sections;
}

/**
 * A gradebook rendered as a bound ledger book (distinct from the manila-folder
 * ReportFolderCard): a green spine, a title-led cover with the grade stamped
 * beneath it, stacked page edges on the fore-edge, and a dark binding base that
 * carries the student + assessment counts. The subject text is the only element
 * that changes colour; the kebab opens edit/delete.
 */
function GradebookCard({ gradebook, onEdit, onDelete }) {
  return (
    <Link
      to={`/gradebooks/${gradebook.id}`}
      className="group block rounded-r-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="relative pb-1.5 pr-2 transition-transform duration-150 group-hover:-translate-y-1">
        {/* fore-edge: stacked page block peeking bottom-right (same technique as ReportFolderCard's silhouette layer) */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute left-0 top-0 aspect-[5/7] w-[216px] translate-x-[7px] translate-y-[6px] rounded-l-md rounded-r-xl bg-neutral-300 dark:bg-white/10" />
          <div className="absolute left-0 top-0 aspect-[5/7] w-[216px] translate-x-[3.5px] translate-y-[3px] rounded-l-md rounded-r-xl bg-neutral-200 dark:bg-white/20" />
        </div>

        {/* the book - the app's Card surface standard, reshaped */}
        <div className="relative grid aspect-[5/7] w-[216px] grid-rows-[1fr_auto] overflow-hidden rounded-l-md rounded-r-xl border border-border border-t-highlight bg-card shadow-card transition-shadow duration-150 group-hover:shadow-card-hover dark:bg-card-gradient">
          {/* spine + hinge shadow */}
          <div aria-hidden className={`absolute inset-y-0 left-0 z-20 w-3.5 rounded-l-md bg-gradient-to-b ${GREEN.spine}`} />
          <div aria-hidden className="absolute inset-y-0 left-3.5 z-20 w-2 bg-gradient-to-r from-black/20 to-transparent" />

          {/* kebab - the only way to edit this gradebook */}
          <div
            className="absolute right-1.5 top-1.5 z-30"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Gradebook options"
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <MoreVertical size={16} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={() => onEdit(gradebook)}>
                  <LucidePencil size={14} className="mr-2" /> Edit gradebook
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onDelete(gradebook)}
                  className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                >
                  <LucideTrash2 size={14} className="mr-2" /> Delete gradebook
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* cover */}
          <div className="relative z-10 flex flex-col pl-7 pr-4 pt-5">
            <h3 className={`line-clamp-2 pr-7 font-display text-[19px] font-semibold leading-snug tracking-tight [overflow-wrap:anywhere] ${titleColorClass(gradebook.titleColor)}`}>
              {gradebook.subject}
            </h3>
            <span className={`mt-3 inline-flex items-baseline gap-1.5 self-start rounded-lg border px-2.5 py-1 ${GREEN.chip}`}>
              <span className="text-[9px] font-bold uppercase tracking-[0.16em] opacity-80">Grade</span>
              <span className="font-display text-[17px] font-bold leading-none">{gradebook.grade}</span>
            </span>
            <div className="min-h-[16px] flex-1" />
          </div>

          {/* binding base - stacked counts (honours the book's bottom binding) */}
          <div className="relative z-10 flex flex-col gap-2.5 border-t border-white/10 bg-neutral-900 py-3 pl-7 pr-4 dark:bg-background">
            <div className="flex items-center gap-2.5">
              <Users size={16} className={GREEN.icon} />
              <span className="text-[15px] font-bold tabular-nums text-neutral-0">{gradebook.studentCount}</span>
              <span className="text-[12px] text-neutral-400">students</span>
            </div>
            <div className="flex items-center gap-2.5 border-t border-white/10 pt-2.5">
              <ClipboardList size={16} className={GREEN.icon} />
              <span className="text-[15px] font-bold tabular-nums text-neutral-0">{gradebook.assessmentCount}</span>
              <span className="text-[12px] text-neutral-400">assessments</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Gradebooks() {
  const { gradebooks, loading, error, refetch } = useGradebooks();
  const api = useGradebookApi();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const sorted = [...gradebooks].sort((a, b) => yearSortKey(b.schoolYear) - yearSortKey(a.schoolYear));
  const sections = groupByYear(sorted);

  // The list is small and server-owned (it carries student/assessment counts),
  // so each mutation refetches rather than patching a local copy - there is no
  // typing latency here to protect, unlike the sheet.
  async function handleCreate(values) {
    setCreating(false);
    await api.createGradebook(values);
    refetch();
  }

  async function handleSave(gradebook, changes) {
    setEditing(null);
    await api.updateGradebook(gradebook.id, changes);
    refetch();
  }

  async function handleDelete() {
    const { id } = deleting; // captured before the modal closes
    setDeleting(null);
    await api.deleteGradebook(id);
    refetch();
  }

  return (
    <PageContainer>
      <PageHeader
        title="Gradebooks"
        subtitle="Track marks for each of your classes."
        action={<Button onClick={() => setCreating(true)}><Plus size={16} /> New</Button>}
      />

      {loading ? (
        <SpinnerPage message="Loading your gradebooks..." />
      ) : error ? (
        /* Without this branch a failed load falls through to the empty state and
           tells a teacher with 30 gradebooks that they have none. */
        <EmptyState
          icon={ClipboardList}
          title="Couldn't load your gradebooks"
          description={error.message}
          action={<Button onClick={refetch}>Try again</Button>}
        />
      ) : gradebooks.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="No gradebooks yet"
          description="Create your first gradebook to start recording marks."
          action={<Button onClick={() => setCreating(true)}><Plus size={16} /> New Gradebook</Button>}
        />
      ) : (
        <div className="space-y-5">
          {sections.map(({ year, items }) => (
            <div key={year ?? "__no_year__"} className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-0.5 flex-1 bg-border" />
                <span className="text-lg font-bold text-foreground">{year ?? "No Year"}</span>
                <div className="h-0.5 flex-1 bg-border" />
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-8">
                {items.map((gb) => (
                  <GradebookCard key={gb.id} gradebook={gb} onEdit={setEditing} onDelete={setDeleting} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateGradebookModal open={creating} onClose={() => setCreating(false)} onCreate={handleCreate} />
      <EditGradebookModal gradebook={editing} onClose={() => setEditing(null)} onSave={handleSave} />
      <DeleteGradebookModal gradebook={deleting} onClose={() => setDeleting(null)} onConfirm={handleDelete} />
    </PageContainer>
  );
}
